import { buttonStyle } from '@florencelabs/button';
import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { resolveSubdirectoryEmptyRedirectUrl } from '@provider-directory/functions/resolve-subdirectory-empty-redirect-url';
import { getDeploymentEnvironment } from '@provider-directory/services/getEnvironmentVariables';
import type { SubdirectoryEmptyRedirect } from '@provider-directory/types/runtime-config.contracts';

export const NoneInSubDirectory = ({
  subdirectoryEmptyRedirect,
  specialtyName,
}: {
  subdirectoryEmptyRedirect: SubdirectoryEmptyRedirect;
  specialtyName: string;
}): ReactElement => {
  const { t } = useTranslation();
  const {
    config: { name },
  } = useOrgContext();
  const { parentDisplayName, parentProvidersUrl } = subdirectoryEmptyRedirect;

  const pathname = usePathname();
  const specialty = pathname.split('/').pop();

  const parentBasePath = resolveSubdirectoryEmptyRedirectUrl(
    parentProvidersUrl,
    getDeploymentEnvironment(),
  );

  return (
    <div
      className={clsx(
        'flex h-min flex-col items-center gap-6 rounded-lg bg-primary-100 mix-blend-multiply tablet:col-span-3',
        'tablet:px-18 px-8 py-10 tablet:py-20',
      )}
    >
      <div className={'text-primary-color'} aria-hidden={'true'}>
        <MaterialSymbol name={'error_fill'} size={40} />
      </div>
      <div className={'flex flex-col items-center gap-2'}>
        <span className={'text-heading-xlarge font-semibold'}>
          {t('searchResults.noResultsTitle')}
        </span>
        <div className={'max-w-prose text-center'}>
          <div>
            {t('searchResults.nonInSubDirectory', {
              healthSystem: name,
              specialty: specialtyName,
              parentHealthSystem: parentDisplayName,
            }).replace(/&amp;|&#x2F;/g, (match) =>
              match === '&amp;' ? '&' : '/',
            )}
            {/* ^ avoids i18n html sanitation for '/' and '&' */}
          </div>
        </div>
      </div>
      <Link
        className={buttonStyle({ variant: 'clientMain', size: 'small' })}
        href={`${parentBasePath}/specialty/${specialty}`}
        target={'_blank'}
      >
        {t('searchResults.goToParentHealthSystem', {
          parentHealthSystem: parentDisplayName,
        })}
      </Link>
    </div>
  );
};
